'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { ulid } from 'ulid';
import { DOMPurify } from '@shared/get-html';
import { useBuyerBrandStore } from 'app/store/brand';
import { type GetPageMetaReturn } from 'apis/page';
import { FEATURE_FLAG_KEYS, UI_LINK_TYPE_ENUM } from 'shop-ui/constants';
import useEmblaCarousel from 'embla-carousel-react';
import { getS3ObjectUrl, getDynamicImageSource, getStaticImageSource } from 'libs/url';
import { type BasicShopUIPropTypes } from 'shop-ui/types';

const defaultImageUrl = getS3ObjectUrl(
    '/static/default-image-upload-placeholder-small.jpg'
);

export const useCarousel = (
    props: BasicShopUIPropTypes,
    carouselRef: React.RefObject<HTMLDivElement>
) => {
    const { styleMap } = props;

    const [selectedIndex, setSelectedIndex] = useState(0);

    const cache = useBuyerBrandStore((api) => api.cache);

    const [pageMetaMap, setPageMetaMap] = useState<Record<string, GetPageMetaReturn>>({});

    const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
        loop: true,
    });

    const onSelect = useCallback(() => {
        if (!emblaMainApi) {
            return;
        }

        setSelectedIndex(emblaMainApi.selectedScrollSnap());
    }, [emblaMainApi, setSelectedIndex]);

    const slides = useMemo(() => {
        if (!styleMap) {
            return [];
        }

        const carouselItems = (styleMap[FEATURE_FLAG_KEYS.CAROUSEL_MEDIA_LIST] ||
            []) as string[];

        const carouselDefaultItems = (styleMap[
            FEATURE_FLAG_KEYS.READONLY_CAROUSEL_MEDIA_LIST
        ] || []) as string[];

        const carouselItemsLinkTypeList = (styleMap[
            FEATURE_FLAG_KEYS.CAROUSEL_LINK_TYPE_LIST
        ] || []) as string[];

        const carouselItemsLinkList = (styleMap[FEATURE_FLAG_KEYS.CAROUSEL_LINK_LIST] ||
            []) as string[];

        const carouselItemsShouldOpenNewWindowList = (styleMap[
            FEATURE_FLAG_KEYS.CAROUSEL_SHOULD_OPEN_NEW_WINDOW_LIST
        ] || []) as string[];

        const maxSettingLength = Math.max(
            carouselItems.length,
            carouselItemsLinkTypeList.length,
            carouselItemsLinkList.length,
            carouselItemsShouldOpenNewWindowList.length
        );

        return Array.from({ length: maxSettingLength || 1 }, (_, index) => {
            const originalSource = carouselItems[index];

            const defaultItem = carouselDefaultItems[index]
                ? getStaticImageSource(carouselDefaultItems[index])
                : getStaticImageSource(defaultImageUrl);

            const sources = originalSource
                ? getDynamicImageSource(originalSource)
                : defaultItem;

            const linkType = carouselItemsLinkTypeList[index];

            const link = carouselItemsLinkList[index];

            const shouldOpenNewWindow = carouselItemsShouldOpenNewWindowList[index];

            return {
                id: ulid(),
                sources: [sources],
                linkType,
                link,
                shouldOpenNewWindow,
            };
        });
    }, [styleMap]);

    useEffect(() => {
        if (slides.length) {
            slides.forEach((slide) => {
                if (slide && slide.linkType === UI_LINK_TYPE_ENUM.PAGE && slide.link) {
                    const pageId = slide.link;

                    const pageSegment = cache.pageSegment[pageId];

                    if (pageSegment) {
                        const _href = pageSegment === 'home' ? '/' : `/${pageSegment}`;

                        setPageMetaMap((prev) => ({
                            ...prev,
                            [slide.link]: {
                                ...prev[slide.link],
                                segment: DOMPurify.sanitize(_href),
                            },
                        }));
                    }
                }
            });
        }
    }, [slides, cache, setPageMetaMap]);

    const displaySlides = useMemo(() => {
        return slides.map((slide) => {
            if (
                slide.linkType === UI_LINK_TYPE_ENUM.PAGE &&
                slide.link &&
                pageMetaMap[slide.link]
            ) {
                return {
                    ...slide,
                    link:
                        pageMetaMap[slide.link].segment === 'home'
                            ? '/'
                            : `/${DOMPurify.sanitize(pageMetaMap[slide.link].segment)}`,
                };
            }

            if (slide.linkType === UI_LINK_TYPE_ENUM.ANCHOR && slide.link) {
                return {
                    ...slide,
                    link: `#${slide.link}`,
                };
            }

            return {
                ...slide,
                link: DOMPurify.sanitize(slide.link),
            };
        });
    }, [slides, pageMetaMap]);

    const onDotButtonClick = useCallback(
        (index: number) => {
            if (!emblaMainApi) {
                return;
            }

            emblaMainApi.scrollTo(index);
        },
        [emblaMainApi]
    );

    useEffect(() => {
        if (!emblaMainApi) {
            return;
        }

        onSelect();

        emblaMainApi.on('select', onSelect);
        emblaMainApi.on('reInit', onSelect);
    }, [emblaMainApi, onSelect]);

    useEffect(() => {
        const element = carouselRef as React.RefObject<HTMLDivElement>;

        if (element?.current && typeof window !== 'undefined' && window) {
            const container = element.current.closest(
                '[data-shop-component-container-inner]'
            ) as HTMLElement;

            if (container && styleMap?.[FEATURE_FLAG_KEYS.WITHOUT_CONTAINER_WIDTH]) {
                container.style.setProperty('--shop-ui-custom-site-width', '100%');
                container.style.setProperty(
                    '--shop-ui-custom-container-overflow',
                    'hidden'
                );
            } else {
                container.style.removeProperty('--shop-ui-custom-site-width');
                container.style.removeProperty('--shop-ui-custom-container-overflow');
            }
        }
    }, [carouselRef, styleMap]);

    const state = {
        selectedIndex,
        slides: displaySlides,
        emblaMainRef,
        onDotButtonClick,
    };

    const action = {
        emblaMainApi,
        onDotButtonClick,
    };

    return { state, action };
};

export type UseCarouselReturnType = ReturnType<typeof useCarousel>;
